import {
  LEGEND_PADDING,
  LEGEND_ROW_HEIGHT,
  LEGEND_SWATCH_RADIUS,
  LEGEND_FONT_SIZE,
  LEGEND_BORDER_RADIUS,
  LEGEND_BORDER_WIDTH,
  LEGEND_BACKGROUND_FILL,
  LEGEND_BACKGROUND_OPACITY,
  LEGEND_BORDER_STROKE,
  LEGEND_TEXT_COLOR,
  LEGEND_FONT_FAMILY,
  LEGEND_HEADER_BOTTOM_GAP,
  LEGEND_LABEL_START_X,
  LEGEND_VALUE_END_X,
} from "./constants";
import { computeLegendDimensions } from "./computeLegendDimensions";
import type { LegendBoxProps } from "./types";

const rgbToCss = (rgb: [number, number, number]): string =>
  `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;

export const LegendBox = ({ x, y, items, scale, summary }: LegendBoxProps) => {
  const { width, height } = computeLegendDimensions({
    itemCount: items.length,
    scale,
  });

  const padding = LEGEND_PADDING * scale;
  const rowHeight = LEGEND_ROW_HEIGHT * scale;
  const swatchRadius = LEGEND_SWATCH_RADIUS * scale;
  const fontSize = LEGEND_FONT_SIZE * scale;
  const borderRadius = LEGEND_BORDER_RADIUS * scale;
  const borderWidth = LEGEND_BORDER_WIDTH * scale;
  const headerBottomGap = LEGEND_HEADER_BOTTOM_GAP * scale;

  const labelStartX = LEGEND_LABEL_START_X * scale;
  const valueEndX = LEGEND_VALUE_END_X * scale;

  const headerCenterY = padding + rowHeight / 2;
  const headerBaselineY = headerCenterY + fontSize / 3;
  const separatorY = padding + rowHeight;
  const itemsTopOffset = padding + rowHeight + headerBottomGap;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={borderRadius}
        ry={borderRadius}
        fill={LEGEND_BACKGROUND_FILL}
        fillOpacity={LEGEND_BACKGROUND_OPACITY}
        stroke={LEGEND_BORDER_STROKE}
        strokeWidth={borderWidth}
      />
      <text
        x={padding}
        y={headerBaselineY}
        fontFamily={LEGEND_FONT_FAMILY}
        fontSize={fontSize}
        fontWeight={600}
        fill={LEGEND_TEXT_COLOR}
      >
        {summary.left}
      </text>
      <text
        x={valueEndX}
        y={headerBaselineY}
        fontFamily={LEGEND_FONT_FAMILY}
        fontSize={fontSize}
        fontWeight={600}
        fill={LEGEND_TEXT_COLOR}
        textAnchor="end"
      >
        {summary.right}
      </text>
      <line
        x1={padding}
        y1={separatorY}
        x2={width - padding}
        y2={separatorY}
        stroke={LEGEND_BORDER_STROKE}
        strokeWidth={borderWidth}
      />
      {items.map((item, rowIndex) => {
        const rowCenterY =
          itemsTopOffset + rowIndex * rowHeight + rowHeight / 2;
        const textBaselineY = rowCenterY + fontSize / 3;
        return (
          <g key={`${item.label}-${rowIndex}`}>
            <circle
              cx={padding + swatchRadius}
              cy={rowCenterY}
              r={swatchRadius}
              fill={rgbToCss(item.rgb)}
            />
            <text
              x={labelStartX}
              y={textBaselineY}
              fontFamily={LEGEND_FONT_FAMILY}
              fontSize={fontSize}
              fill={LEGEND_TEXT_COLOR}
            >
              {item.label}
            </text>
            <text
              x={valueEndX}
              y={textBaselineY}
              fontFamily={LEGEND_FONT_FAMILY}
              fontSize={fontSize}
              fill={LEGEND_TEXT_COLOR}
              textAnchor="end"
            >
              {item.value}
            </text>
          </g>
        );
      })}
    </g>
  );
};
